import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import TFIActions from 'notebook/tfi/TFIActions';
import {TFIFileMeta} from 'notebook/tfi/TFIConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import fuzzydate from 'util/fuzzydate';
import * as modals from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';
import {REQUEST_TOOK_TOO_LONG} from 'util/TaskRunner';

export function isCurrentUserLocalSyncFile(tfiFileMeta: TFIFileMeta): boolean {
  return Boolean(BenchlingConfig.ELN_ENABLE_DESKTOP_SYNC && tfiFileMeta && tfiFileMeta.editURL);
}

export async function confirmTFIUnlock(tfiFileMeta: TFIFileMeta): Promise<boolean> {
  const currentUserIsOwner = isCurrentUserLocalSyncFile(tfiFileMeta);
  const lockingUser = tfiFileMeta.tfiAccount.user.handle;

  const {acceptDisabled, warning} = await _getUnlockWarning(tfiFileMeta);

  let timeAgo;
  let lastAction;
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (tfiFileMeta.lastSyncSuccess !== null) {
    lastAction = 'last synced';
    timeAgo = fuzzydate(tfiFileMeta.lastSyncSuccess, {compact: false});
  } else {
    lastAction = 'checked out';
    timeAgo = fuzzydate(tfiFileMeta.syncStatusUpdated, {compact: false});
  }

  let title;
  let confirmText;
  let acceptText;
  let buttonStyle;
  if (currentUserIsOwner) {
    title = 'Check in file';
    acceptText = 'Check in';
    confirmText = (
      <span>
        <p className="u-moderate-spacing-bottom ">
          This file was {lastAction} {timeAgo}. Checking this file in will allow other users to edit it.
        </p>
        {warning}
      </span>
    );
    buttonStyle = ButtonStyle.SECONDARY;
  } else {
    title = 'Unlock file';
    acceptText = 'Unlock';
    confirmText = (
      <span>
        <p>
          {lockingUser} edited this file {timeAgo}.
        </p>
        {warning}
      </span>
    );
    buttonStyle = ButtonStyle.NEGATIVE;
  }
  const confirm = await Promise.resolve(
    modals.confirm(title, confirmText, buttonStyle, {
      promiseCompatible: true,
      enableRedesign: true,
      acceptText,
      acceptDisabled,
    })
  );
  return Boolean(confirm);
}

interface UnlockWarningResult {
  acceptDisabled: boolean;
  warning: JSX.Element;
}

async function _getUnlockWarning(tfiFileMeta: TFIFileMeta): Promise<UnlockWarningResult> {
  const currentUserIsOwner = isCurrentUserLocalSyncFile(tfiFileMeta);
  const lockingUser = tfiFileMeta.tfiAccount.user.handle;
  if (!currentUserIsOwner) {
    return {
      acceptDisabled: false,
      warning: (
        <p>
          Unlocking this file will allow other users to edit it but will prevent {lockingUser} from saving
          further edits. Please check with {lockingUser} before unlocking.
        </p>
      ),
    };
  }
  let upstreamThirdpartyFileRev: string | undefined;
  try {
    upstreamThirdpartyFileRev = await withLoadingScreen(
      // We specifically want to show an error toast here since this is a blocking check for checking in the file
      Promise.resolve(TFIActions.updateUpstreamThirdpartyFileRev(tfiFileMeta.id)),
      'Checking for updates...'
    );
  } catch (e) {
    // We want to catch the task timeout and show the modal to the user. For any other error, we still throw
    if (e.userMessage !== REQUEST_TOOK_TOO_LONG) {
      throw e;
    }
  }
  const hasUpstreamChanges =
    upstreamThirdpartyFileRev == null || upstreamThirdpartyFileRev !== tfiFileMeta.thirdpartyFileRev;
  if (hasUpstreamChanges) {
    return {
      acceptDisabled: true,
      warning: (
        <span>
          <p className="alert alert-danger u-small-padding">
            <BenchlingIcon icon="exclamation-circle" className="u-mini-spacing-right" />
            Edits to the file were not automatically synced. If you see this message frequently,{' '}
            <a
              href={`mailto:${_.compact([
                BenchlingConfig.CUSTOMER_ADMIN_EMAIL,
                BenchlingConfig.SUPPORT_EMAIL_ADDRESS,
              ]).join(';')}`}
            >
              let us know.
            </a>
          </p>
          <p>To manually sync and check in this file:</p>
          <ol>
            <li>Hit Cancel.</li>
            <li>
              Click the sync icon ({' '}
              <a className="u-no-mouse-target">
                <BenchlingIcon icon="refresh" />
              </a>{' '}
              ) on the file attachment.
            </li>
            <li>
              Click the check-in icon ({' '}
              <a className="u-no-mouse-target">
                <BenchlingIcon icon="floppy-disk-arrow-up" />
              </a>{' '}
              ) on the file attachment again.
            </li>
          </ol>
        </span>
      ),
    };
  }
  if (tfiFileMeta.lastSyncSuccess == null) {
    return {
      acceptDisabled: false,
      warning: (
        <p className="alert alert-danger u-small-padding u-tiny-spacing-bottom">
          <BenchlingIcon icon="exclamation-circle" className="u-mini-spacing-right" />
          No edits were detected since checking out the file. If edits were made, see{' '}
          <a href="https://help.benchling.com/hc/articles/9684279979021">this help article</a> to
          troubleshoot.
        </p>
      ),
    };
  }
  return {
    acceptDisabled: false,
    warning: (
      <p className="alert alert-warning u-small-padding u-tiny-spacing-bottom">
        <BenchlingIcon icon="exclamation-circle" className="u-mini-spacing-right" />
        Please be sure to close your copy of the file, as further edits will no longer be synced.
      </p>
    ),
  };
}
